import type { TokenCredential } from "@azure/identity";
import { serializeBrainLink, IN_CONTAINER_CREDENTIAL_REF, type BrainLink } from "@m8t-stack/api-contract";
import { getAgentVersion } from "./foundry-agent-get.js";
import { createCoderVersion, getAgentIdentityPrincipalId } from "./foundry-agents.js";
import { grantFoundryUser, grantKeyVaultSecretsUser, resolveKeyVaultResourceId } from "./rbac.js";
import { mirrorBrainYaml } from "./brain-yaml-mirror.js";
import { LocalCliError } from "./errors.js";
import type { FoundryProject } from "./foundry-project.js";

// Reasoning-family deployment-name whitelist (gpt-5*, o1–o9).
const REASONING_RE = /^(gpt-5|o[1-9])/i;

export interface EnableHostedBrainArgs {
  credential: TokenCredential;
  subscriptionId: string;
  project: FoundryProject;
  kvUri: string;
  agentName: string;
  repo: string;
  branch: string;
  installationId: string;
  modelDeployment?: string;
  allowNonReasoning?: boolean;
  onProgress?: (m: string) => void;
}

export interface EnableHostedBrainResult { version?: string; alreadyLinked: boolean }

export async function enableHostedBrain(args: EnableHostedBrainArgs): Promise<EnableHostedBrainResult> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop progress stub
  const progress = args.onProgress ?? ((_m: string) => {});

  const current = await getAgentVersion({
    credential: args.credential,
    projectEndpoint: args.project.endpoint,
    agentName: args.agentName,
  });
  if (current.definition.kind !== "hosted") {
    throw new LocalCliError({
      code: "NOT_A_HOSTED_AGENT",
      message: `enableHostedBrain: '${args.agentName}' is kind '${current.definition.kind}', not hosted.`,
    });
  }

  const def = current.definition as Record<string, unknown>;
  const currentEnv: Partial<Record<string, string>> = { ...((def.environment_variables ?? {}) as Record<string, string>) };

  const effectiveModel = args.modelDeployment ?? currentEnv.MODEL_DEPLOYMENT_NAME ?? "";
  if (!REASONING_RE.test(effectiveModel) && !args.allowNonReasoning) {
    progress(
      `warning: '${effectiveModel || "(unset)"}' is not a recognized reasoning model (gpt-5*/o*). ` +
        `Brain workers write reliably only with reasoning + low effort; proceeding anyway. ` +
        `Pass --allow-non-reasoning to silence, or --model-deployment gpt-5-mini to switch.`,
    );
  }

  const link: BrainLink = {
    repo: args.repo,
    branch: args.branch,
    topology: "per-worker",
    schemaVersion: "1",
    credentialRef: IN_CONTAINER_CREDENTIAL_REF,
    installationId: args.installationId,
  };

  const existing = parseBrain(current.metadata?.brain);
  if (
    existing?.repo === args.repo &&
    existing.installationId === args.installationId &&
    existing.branch === args.branch &&
    currentEnv.BRAIN_REPO === args.repo
  ) {
    progress("already linked — no-op (no new version, no token rotation).");
    return { alreadyLinked: true };
  }

  const env: Record<string, string> = {
    ...currentEnv,
    BRAIN_REPO: args.repo,
    BRAIN_BRANCH: args.branch,
    GITHUB_APP_INSTALLATION_ID: args.installationId,
    AZURE_KEYVAULT_URI: args.kvUri,
    REASONING_EFFORT: "low",
    MODEL_DEPLOYMENT_NAME: effectiveModel,
  };

  progress("creating brain-enabled hosted version…");
  const version = await createCoderVersion({
    credential: args.credential,
    endpoint: args.project.endpoint,
    name: args.agentName,
    image: def.image as string,
    cpu: def.cpu as string,
    memory: def.memory as string,
    env,
    metadata: {
      // Spread the current metadata first so unmanaged keys (a2a, a2aCard, …)
      // survive the relink version; the managed fields are re-asserted over the
      // spread, and createCoderVersion applies the `brain` arg last.
      ...current.metadata,
      source: "m8t-stack-POC",
      kind: "hosted",
      persona: current.metadata?.persona ?? "coding-agent",
      personaVersion: current.metadata?.personaVersion ?? null,
    },
    brain: serializeBrainLink(link),
  });

  progress("granting the agent identity Foundry User…");
  const principalId = await getAgentIdentityPrincipalId({
    credential: args.credential,
    endpoint: args.project.endpoint,
    name: args.agentName,
    version,
  });
  await grantFoundryUser({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    accountScope: args.project.accountScope,
    principalId,
  });

  progress("granting the agent identity Key Vault Secrets User…");
  const kvScope = await resolveKeyVaultResourceId({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    kvUri: args.kvUri,
  });
  await grantKeyVaultSecretsUser({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    kvScope,
    principalId,
  });

  progress("mirroring .m8t/brain.yaml…");
  await mirrorBrainYaml({
    credential: args.credential,
    kvUri: args.kvUri,
    installationId: args.installationId,
    repo: args.repo,
    branch: args.branch,
    agent: args.agentName,
    persona: current.metadata?.persona ?? args.agentName,
  });

  return { version, alreadyLinked: false };
}

function parseBrain(raw: string | undefined): BrainLink | undefined {
  if (!raw) return undefined;
  try { return JSON.parse(raw) as BrainLink; } catch { return undefined; }
}
